import { apiFetch } from '@/platform/lib/api';

export type OrderStatus =
  | 'pending_payment'
  | 'paid'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

export type ShippingAddress = {
  name?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type VendorOrder = {
  id: string;
  sellerUserId: string;
  buyerUserId: string;
  buyerEmail?: string;
  status: OrderStatus;
  fulfillmentStatus: string;
  lineItems: Array<{
    productId: string;
    name: string;
    sku?: string;
    unitPriceCents: number;
    quantity: number;
    imageUrl?: string;
  }>;
  subtotalCents: number;
  platformFeeCents: number;
  shippingCents?: number;
  taxCents?: number;
  totalCents: number;
  currency: string;
  shippingAddress?: ShippingAddress;
  shippingOptionLabel?: string;
  trackingCarrier?: string;
  trackingNumber?: string;
  createdAt: string;
  paidAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
};

export type SellerCartLineItem = {
  productId: string;
  name: string;
  sku?: string;
  unitPriceCents: number;
  quantity: number;
  imageUrl?: string;
};

export type SellerCart = {
  orderId: string;
  sellerUserId: string;
  status: 'pending_payment';
  lineItems: SellerCartLineItem[];
  subtotalCents: number;
  currency: string;
};

export type CartMutationResult = {
  cart: SellerCart | null;
  added?: Array<{ productId: string; quantity: number }>;
  skipped?: Array<{ productId: string; reason: string }>;
};

export type OrderMessage = {
  id: string;
  authorRole: 'buyer' | 'seller';
  source: 'web' | 'email_inbound';
  body: string;
  authorUserId?: string;
  createdAt: string;
};

/** @deprecated Use cart checkout flow instead. */
export function createCheckoutSession(body: {
  sellerUserId: string;
  productId: string;
  quantity: number;
  acceptedMarketplacePurchaseTerms: boolean;
}) {
  return apiFetch<{ orderId: string; checkoutUrl: string }>('/commerce/checkout-sessions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getSellerCart(sellerUserId: string) {
  return apiFetch<{ cart: SellerCart | null }>(`/commerce/carts/${sellerUserId}`);
}

export function addCartItem(sellerUserId: string, body: { productId: string; quantity: number }) {
  return apiFetch<CartMutationResult>(`/commerce/carts/${sellerUserId}/items`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateCartItem(sellerUserId: string, productId: string, body: { quantity: number }) {
  return apiFetch<CartMutationResult>(`/commerce/carts/${sellerUserId}/items/${productId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function removeCartItem(sellerUserId: string, productId: string) {
  return apiFetch<CartMutationResult>(`/commerce/carts/${sellerUserId}/items/${productId}`, {
    method: 'DELETE',
  });
}

export function clearSellerCart(sellerUserId: string) {
  return apiFetch<{ cart: null }>(`/commerce/carts/${sellerUserId}`, { method: 'DELETE' });
}

export function checkoutSellerCart(
  sellerUserId: string,
  body: { acceptedMarketplacePurchaseTerms: boolean },
) {
  return apiFetch<{ orderId: string; checkoutUrl: string }>(
    `/commerce/carts/${sellerUserId}/checkout`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

export function reorderIntoCart(sellerUserId: string, sourceOrderId: string) {
  return apiFetch<CartMutationResult>(`/commerce/carts/${sellerUserId}/reorder`, {
    method: 'POST',
    body: JSON.stringify({ sourceOrderId }),
  });
}

export function attachGuestAfterLogin() {
  return apiFetch<{ attachedOrders: number; mergedCarts: number }>('/commerce/guest/attach-after-login', {
    method: 'POST',
    body: '{}',
  });
}

export function listMyOpenCarts() {
  return apiFetch<{ carts: VendorOrder[] }>('/commerce/open-carts');
}

export function listMyOrders(params?: { cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ orders: VendorOrder[]; nextCursor?: string }>(`/orders/mine${qs ? `?${qs}` : ''}`);
}

export function getOrder(orderId: string) {
  return apiFetch<VendorOrder>(`/orders/${orderId}`);
}

export function listOrderMessages(orderId: string) {
  return apiFetch<{ messages: OrderMessage[] }>(`/commerce/orders/${orderId}/messages`);
}

export function postOrderMessage(orderId: string, body: string) {
  return apiFetch<OrderMessage>(`/commerce/orders/${orderId}/messages`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export function listVendorOrders(vendorId: string, params?: { status?: string; cursor?: string }) {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiFetch<{ orders: VendorOrder[]; nextCursor?: string }>(
    `/vendors/${vendorId}/orders${qs ? `?${qs}` : ''}`,
  );
}

export function getVendorOrder(vendorId: string, orderId: string) {
  return apiFetch<VendorOrder>(`/vendors/${vendorId}/orders/${orderId}`);
}

export function updateVendorOrderFulfillment(
  vendorId: string,
  orderId: string,
  body: { status?: OrderStatus; trackingCarrier?: string; trackingNumber?: string },
) {
  return apiFetch<VendorOrder>(`/vendors/${vendorId}/orders/${orderId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function refundVendorOrder(vendorId: string, orderId: string, reason?: string) {
  return apiFetch<VendorOrder>(`/vendors/${vendorId}/orders/${orderId}/refund`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function getPayoutSummary(vendorId: string) {
  return apiFetch<{ availableCents: number; pendingCents: number; currency: string }>(
    `/vendors/${vendorId}/payouts/summary`,
  );
}

export function formatOrderPrice(cents: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export function formatShippingAddress(addr: ShippingAddress): string {
  const parts = [
    addr.name,
    addr.line1,
    addr.line2,
    [addr.city, addr.state, addr.postalCode].filter(Boolean).join(', '),
    addr.country,
  ].filter(Boolean);
  return parts.join('\n');
}

export function cartItemCount(cart: SellerCart | null | undefined): number {
  if (!cart?.lineItems?.length) return 0;
  return cart.lineItems.reduce((sum, line) => sum + line.quantity, 0);
}

export function cartLineForProduct(cart: SellerCart | null | undefined, productId: string) {
  return cart?.lineItems.find((line) => line.productId === productId);
}
